import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { BrandMark } from "@/components/BrandMark";
import { ResendCode } from "@/components/ResendCode";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login, loginMobile, setSession } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("mobile"); // mobile | central | email
  const [view, setView] = useState("login"); // login | activate | activate-code | forgot | forgot-code
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mobile, setMobile] = useState("");
  const [pin, setPin] = useState("");
  const [remember, setRemember] = useState(true);
  const [code, setCode] = useState("");
  const [newPin, setNewPin] = useState("");
  const [newPin2, setNewPin2] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [activationEmail, setActivationEmail] = useState("");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = (v) => {
    setView(v);
    setError("");
    setNotice("");
    setCode("");
    setNewPin("");
    setNewPin2("");
  };

  const run = async (fn) => {
    setBusy(true);
    setError("");
    try {
      await fn();
    } catch (e) {
      setError(apiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (mode !== "email") await loginMobile(mobile, pin, remember);
      else await login(email, password, remember);
      nav("/dashboard");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.code === "activation_required") {
        reset("activate");
      } else {
        setError(apiError(detail) || err.message);
      }
    } finally {
      setBusy(false);
    }
  };

  const sendActivationCode = () =>
    run(async () => {
      const { data } = await api.post("/auth/activate/start", { mobile, email: activationEmail });
      if (data.needs_email) {
        setError("Enter your email address so we can send your verification code.");
        return;
      }
      setMaskedEmail(data.email);
      setNotice(
        data.sent
          ? `We've emailed a 6-digit activation code to ${data.email}.`
          : `Activation code generated for ${data.email}, but email delivery isn't configured yet — ask your admin.`,
      );
      setView("activate-code");
    });

  const verifyActivation = () =>
    run(async () => {
      const { data } = await api.post("/auth/activate/verify", { mobile, code });
      setSession(data); // email verified; setup gate will now ask for a PIN
      nav("/dashboard");
    });

  const sendResetCode = () =>
    run(async () => {
      const { data } = await api.post("/auth/forgot-pin", { mobile });
      setMaskedEmail(data.email);
      setNotice(
        data.sent
          ? `We've emailed a 6-digit reset code to ${data.email}.`
          : `Reset code generated for ${data.email}, but email delivery isn't configured yet — ask your admin.`,
      );
      setView("forgot-code");
    });

  const doReset = () =>
    run(async () => {
      if (newPin !== newPin2) {
        setError("The two PINs do not match");
        return;
      }
      if (!/^\d{4}$/.test(newPin)) {
        setError("PIN must be exactly 4 digits");
        return;
      }
      await api.post("/auth/reset-pin", { mobile, code, new_pin: newPin });
      reset("login");
      setPin("");
      setNotice("PIN updated. Sign in with your new PIN.");
    });

  const subtitle =
    view === "activate" || view === "activate-code"
      ? "Activate your account — verify your email to continue."
      : view === "forgot" || view === "forgot-code"
        ? "Reset your PIN using a code sent to your email."
        : mode === "email"
          ? "Sign in with your email and password."
          : mode === "central"
            ? "Central team members sign in with mobile number and PIN."
            : "Studio teams sign in with mobile number and PIN.";

  return (
    <div className="grid min-h-screen bg-background lg:grid-cols-2">
      <div className="flex items-center justify-center px-6 py-12 sm:py-16">
        <div className="fade-in w-full max-w-sm">
          <div className="mb-8 flex items-center justify-between">
            <BrandMark className="h-14" />
            <button
              type="button"
              onClick={() => nav("/")}
              data-testid="login-back-btn"
              className="inline-flex items-center gap-1.5 rounded-full border border-brand-line px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors duration-200 hover:bg-brand-tint"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back to site
            </button>
          </div>
          <h1 className="font-display text-4xl font-semibold text-brand sm:text-5xl">PPF Network</h1>
          <p className="mt-2 text-sm text-slate-500">{subtitle}</p>

          {view === "login" && (
            <>
              <div className="mt-6 inline-flex w-full rounded-md border border-brand-line bg-white p-1">
                {[
                  ["mobile", "Studio login"],
                  ["central", "Central team"],
                  ["email", "Email login"],
                ].map(([k, label]) => (
                  <button
                    key={k}
                    type="button"
                    data-testid={`login-mode-${k}`}
                    onClick={() => {
                      setMode(k);
                      setError("");
                    }}
                    className={`min-h-[44px] flex-1 rounded px-2 text-xs font-medium transition-colors duration-200 sm:text-sm ${
                      mode === k ? "bg-brand text-white" : "text-slate-600 hover:bg-brand-tint"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <form onSubmit={submit} className="mt-6 space-y-4">
                {mode !== "email" ? (
                  <>
                    <div>
                      <Label htmlFor="mobile">Mobile number</Label>
                      <Input
                        id="mobile"
                        data-testid="login-mobile-input"
                        inputMode="numeric"
                        value={mobile}
                        onChange={(e) => setMobile(e.target.value)}
                        required
                        className="mt-1.5 min-h-[44px]"
                        placeholder="10-digit mobile number"
                      />
                    </div>
                    <div>
                      <Label htmlFor="pin">PIN</Label>
                      <Input
                        id="pin"
                        data-testid="login-pin-input"
                        type="password"
                        value={pin}
                        onChange={(e) => setPin(e.target.value)}
                        required
                        className="mt-1.5 min-h-[44px]"
                        placeholder="4-digit PIN or first-time code"
                      />
                      <p className="mt-1.5 text-xs text-slate-500">
                        First time signing in? Enter the temporary code from your admin. You'll then
                        verify your email and set your own PIN.
                      </p>
                      <button
                        type="button"
                        data-testid="forgot-pin-link"
                        onClick={() => {
                          reset("forgot");
                        }}
                        className="mt-1.5 text-xs font-medium text-brand hover:underline"
                      >
                        Forgot PIN?
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        data-testid="login-email-input"
                        type="text"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="mt-1.5 min-h-[44px]"
                        placeholder="you@example.com"
                      />
                    </div>
                    <div>
                      <Label htmlFor="password">Password</Label>
                      <Input
                        id="password"
                        data-testid="login-password-input"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        className="mt-1.5 min-h-[44px]"
                        placeholder="••••••••"
                      />
                    </div>
                  </>
                )}

                <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-600">
                  <input
                    type="checkbox"
                    data-testid="remember-me-checkbox"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                    className="h-4 w-4 rounded border-brand-line accent-brand"
                  />
                  Keep me signed in on this device
                </label>

                {notice && <p className="text-sm font-medium text-emerald-600">{notice}</p>}
                {error && (
                  <p data-testid="login-error" className="text-sm font-medium text-rose-600">
                    {error}
                  </p>
                )}
                <Button data-testid="login-submit" type="submit" className="min-h-[44px] w-full" disabled={busy}>
                  {busy ? "Signing in…" : "Sign in"}
                </Button>
              </form>
            </>
          )}

          {view === "activate" && (
            <div className="mt-6 space-y-4">
              <p className="text-sm text-slate-600">
                Let's activate your account for <span className="font-medium">{mobile}</span>.
                Enter your email address and we'll send a 6-digit verification code.
              </p>
              <div>
                <Label htmlFor="act-email">Email address</Label>
                <Input
                  id="act-email"
                  data-testid="activation-email-input"
                  type="email"
                  value={activationEmail}
                  onChange={(e) => setActivationEmail(e.target.value)}
                  className="mt-1.5 min-h-[44px]"
                  placeholder="you@example.com"
                />
                <p className="mt-1 text-xs text-slate-500">
                  If your admin already registered an email, you can leave this blank.
                </p>
              </div>
              {error && <p className="text-sm font-medium text-rose-600" data-testid="login-error">{error}</p>}
              <Button
                data-testid="send-activation-code"
                className="min-h-[44px] w-full"
                disabled={busy}
                onClick={sendActivationCode}
              >
                {busy ? "Sending…" : "Send verification code"}
              </Button>
              <button type="button" className="w-full text-xs text-slate-500 hover:text-brand" onClick={() => reset("login")}>
                Back to sign in
              </button>
            </div>
          )}

          {view === "activate-code" && (
            <div className="mt-6 space-y-4">
              <p className="text-sm text-slate-600" data-testid="activate-notice">
                {notice || `Enter the 6-digit code we sent to ${maskedEmail}.`}
              </p>
              <div>
                <Label htmlFor="act-code">Activation code</Label>
                <Input
                  id="act-code"
                  data-testid="activation-code-input"
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  className="mt-1.5 min-h-[44px] tracking-[0.4em]"
                  placeholder="••••••"
                />
              </div>
              {error && <p className="text-sm font-medium text-rose-600" data-testid="login-error">{error}</p>}
              <Button
                data-testid="verify-activation"
                className="min-h-[44px] w-full"
                disabled={busy || code.length !== 6}
                onClick={verifyActivation}
              >
                {busy ? "Verifying…" : "Verify & continue"}
              </Button>
              <div className="text-center">
                <ResendCode
                  testid="resend-activation-code"
                  onResend={async () => {
                    const { data } = await api.post("/auth/activate/start", { mobile, email: activationEmail });
                    setMaskedEmail(data.email);
                    setNotice(`We've re-sent an activation code to ${data.email}.`);
                  }}
                />
              </div>
              <button type="button" className="w-full text-xs text-slate-500 hover:text-brand" onClick={() => reset("login")}>
                Back to sign in
              </button>
            </div>
          )}

          {view === "forgot" && (
            <div className="mt-6 space-y-4">
              <div>
                <Label htmlFor="fp-mobile">Mobile number</Label>
                <Input
                  id="fp-mobile"
                  data-testid="forgot-mobile-input"
                  inputMode="numeric"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value)}
                  className="mt-1.5 min-h-[44px]"
                  placeholder="10-digit mobile number"
                />
                <p className="mt-1.5 text-xs text-slate-500">
                  We'll email a reset code to the address on your account.
                </p>
              </div>
              {error && <p className="text-sm font-medium text-rose-600" data-testid="login-error">{error}</p>}
              <Button
                data-testid="send-reset-code"
                className="min-h-[44px] w-full"
                disabled={busy || mobile.length < 10}
                onClick={sendResetCode}
              >
                {busy ? "Sending…" : "Send reset code"}
              </Button>
              <button type="button" className="w-full text-xs text-slate-500 hover:text-brand" onClick={() => reset("login")}>
                Back to sign in
              </button>
            </div>
          )}

          {view === "forgot-code" && (
            <div className="mt-6 space-y-4">
              <p className="text-sm text-slate-600" data-testid="forgot-notice">
                {notice || `Enter the code we sent to ${maskedEmail} and choose a new PIN.`}
              </p>
              <div>
                <Label htmlFor="fp-code">Reset code</Label>
                <Input
                  id="fp-code"
                  data-testid="reset-code-input"
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  className="mt-1.5 min-h-[44px] tracking-[0.4em]"
                  placeholder="••••••"
                />
              </div>
              <div>
                <Label htmlFor="fp-newpin">New 4-digit PIN</Label>
                <Input
                  id="fp-newpin"
                  data-testid="reset-new-pin-input"
                  type="password"
                  inputMode="numeric"
                  maxLength={4}
                  value={newPin}
                  onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ""))}
                  className="mt-1.5 min-h-[44px] tracking-[0.4em]"
                  placeholder="••••"
                />
              </div>
              <div>
                <Label htmlFor="fp-newpin2">Confirm new PIN</Label>
                <Input
                  id="fp-newpin2"
                  data-testid="reset-new-pin2-input"
                  type="password"
                  inputMode="numeric"
                  maxLength={4}
                  value={newPin2}
                  onChange={(e) => setNewPin2(e.target.value.replace(/\D/g, ""))}
                  className="mt-1.5 min-h-[44px] tracking-[0.4em]"
                  placeholder="••••"
                />
              </div>
              {error && <p className="text-sm font-medium text-rose-600" data-testid="login-error">{error}</p>}
              <Button
                data-testid="reset-pin-submit"
                className="min-h-[44px] w-full"
                disabled={busy || code.length !== 6 || newPin.length !== 4}
                onClick={doReset}
              >
                {busy ? "Updating…" : "Update PIN"}
              </Button>
              <div className="text-center">
                <ResendCode
                  testid="resend-reset-code"
                  onResend={async () => {
                    const { data } = await api.post("/auth/forgot-pin", { mobile });
                    setMaskedEmail(data.email);
                    setNotice(`We've re-sent a reset code to ${data.email}.`);
                  }}
                />
              </div>
              <button type="button" className="w-full text-xs text-slate-500 hover:text-brand" onClick={() => reset("login")}>
                Back to sign in
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="relative hidden lg:block">
        <img
          src="https://images.unsplash.com/photo-1620584899131-a5ff5f8fbb03?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBjYXIlMjBkZXRhaWxpbmclMjBwYWludCUyMHByb3RlY3Rpb258ZW58MHx8fHwxNzg3MzAwOTE2fDA&ixlib=rb-4.1.0&q=85"
          alt="Installer applying paint protection film"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-brand-ink/50" />
        <div className="absolute bottom-10 left-10 max-w-sm text-white">
          <p className="font-display text-2xl font-semibold">Ten studios. One standard.</p>
          <p className="mt-2 text-sm text-white/80">
            PPF, ceramic and wrap operations — customers, jobs, stock and profit in one place.
          </p>
        </div>
      </div>
    </div>
  );
}
